/**
 * AttachmentMenu
 * --------------
 * Unified attach-media trigger used by post composer, comment composer,
 * and DM composer. One paperclip button → DropdownMenu with Image /
 * Video / File-Audio items. Replaces 2-3 separate icon buttons per
 * surface. Per-item disabled states preserve existing semantics
 * (e.g. image item disabled at 10/10, file item disabled at 5/5).
 *
 * The component is presentation-only: the file pickers (hidden <input>)
 * and upload pipelines live in the parent. Each menu item just calls
 * the appropriate callback, which triggers the picker.
 */
import React from 'react';
import { Paperclip, Image as ImageIcon, Video as VideoIcon, FileText, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import UploadProgress from '@/components/shared/UploadProgress';

export default function AttachmentMenu({
  onImageClick,
  onVideoClick,
  onFileClick,
  // Per-item disabled controls. Surface passes these based on its own
  // counter caps (e.g. images.length >= 10) and in-flight upload state.
  imageDisabled = false,
  videoDisabled = false,
  fileDisabled = false,
  // If false, the File / Audio item is omitted entirely (comments).
  showFile = true,
  // Poll item — opt-in (DM only). When true, renders a "Poll" entry that calls
  // onPollClick. Feed/comment composers leave these defaulted off.
  showPoll = false,
  onPollClick,
  pollDisabled = false,
  // 0–100 to render a progress ring on the trigger. null = idle.
  uploadProgress = null,
  // For tooltip/aria copy when uploading (e.g. "image", "video", "file").
  activeUploadLabel = '',
  // Disable the trigger entirely (e.g. parent is sending).
  disabled = false,
  // Visual size. 'sm' = matches inline toolbar (post/comment); 'default'
  // = ~40px squared (DM). Surfaces can also pass `triggerClassName` to
  // override exact dimensions.
  size = 'default',
  // Popover alignment relative to the trigger button.
  align = 'start',
  // Forwarded class on the trigger button (lets each surface tune sizing
  // so the trigger lines up with adjacent buttons in its toolbar).
  triggerClassName = '',
  // data-testid prefix → '<prefix>-attach-btn', '<prefix>-attach-image', etc.
  testIdPrefix = 'attach',
  // For DM: editor needs focus preserved when user clicks the trigger.
  // Surface passes `preserveFocus` to enable the mousedown preventDefault.
  preserveFocus = false,
  // Optional callback fired when the dropdown closes (Escape, outside
  // click, or item selection). DM surface uses this to return focus to
  // the rich-text editor so the user can keep typing.
  onClose,
}) {
  const isUploading = uploadProgress !== null;
  const isProcessing = isUploading && uploadProgress >= 90 && uploadProgress < 100;

  const triggerIconSize = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  const ringSize = size === 'sm' ? 16 : 20;

  const label = isUploading
    ? isProcessing
      ? `Processing ${activeUploadLabel || 'upload'}…`
      : `Uploading ${activeUploadLabel || 'attachment'}… ${uploadProgress}%`
    : 'Attach photo, video, or file';

  return (
    // No modal={false} needed here — the shared <DropdownMenu> wrapper
    // (components/ui/dropdown-menu.jsx) already defaults to modal=false
    // so body scroll isn't locked and the page doesn't shift when opened.
    <DropdownMenu onOpenChange={(open) => { if (!open && onClose) onClose(); }}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size={size === 'sm' ? 'sm' : 'icon'}
          aria-label={label}
          title={label}
          disabled={disabled}
          // In DM, prevent the tiptap editor from losing focus when the
          // user mouses-down on the attach trigger.
          onMouseDown={preserveFocus ? (e) => e.preventDefault() : undefined}
          className={
            'text-slate-500 dark:text-slate-400 hover:text-blue-500 dark:hover:text-blue-400 ' +
            'transition-all active:scale-95 ' +
            (isUploading ? 'opacity-90 ' : '') +
            triggerClassName
          }
          data-testid={`${testIdPrefix}-attach-btn`}
        >
          {isUploading ? (
            <UploadProgress progress={uploadProgress} size={ringSize} className="text-blue-500" />
          ) : (
            <Paperclip className={triggerIconSize} />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        sideOffset={6}
        // bg-popover + text-popover-foreground = automatic dark mode via
        // the existing CSS variable system (April 2026 polish pass).
        className="w-44 p-1"
      >
        <DropdownMenuItem
          onClick={onImageClick}
          disabled={imageDisabled}
          data-testid={`${testIdPrefix}-attach-image`}
          className="cursor-pointer gap-2.5 focus:bg-blue-50 dark:focus:bg-blue-500/15 focus:text-blue-700 dark:focus:text-blue-300"
        >
          <ImageIcon className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
          <span className="text-sm">Image</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={onVideoClick}
          disabled={videoDisabled}
          data-testid={`${testIdPrefix}-attach-video`}
          className="cursor-pointer gap-2.5 focus:bg-blue-50 dark:focus:bg-blue-500/15 focus:text-blue-700 dark:focus:text-blue-300"
        >
          <VideoIcon className="w-4 h-4 text-purple-500 dark:text-purple-400" />
          <span className="text-sm">Video</span>
        </DropdownMenuItem>
        {showFile && (
          <DropdownMenuItem
            onClick={onFileClick}
            disabled={fileDisabled}
            data-testid={`${testIdPrefix}-attach-file`}
            className="cursor-pointer gap-2.5 focus:bg-blue-50 dark:focus:bg-blue-500/15 focus:text-blue-700 dark:focus:text-blue-300"
          >
            <FileText className="w-4 h-4 text-amber-500 dark:text-amber-400" />
            <span className="text-sm">File / Audio</span>
          </DropdownMenuItem>
        )}
        {showPoll && (
          <DropdownMenuItem
            onClick={onPollClick}
            disabled={pollDisabled}
            data-testid={`${testIdPrefix}-attach-poll`}
            className="cursor-pointer gap-2.5 focus:bg-blue-50 dark:focus:bg-blue-500/15 focus:text-blue-700 dark:focus:text-blue-300"
          >
            <BarChart3 className="w-4 h-4 text-blue-500 dark:text-blue-400" />
            <span className="text-sm">Poll</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
